// src/frontend/data/books/DarkForest/data/cap7.js

const CAP7 = {
  id: 7,
  title: "Respalda tu billetera",
  sections: [
    {
      heading: "¿Por qué este capítulo importa?",
      text: [
        "Respaldar tu billetera es uno de los pasos más críticos de toda tu vida cripto. Aquí no hay botón de **“¿olvidaste tu contraseña?”** ni soporte que te devuelva los fondos. Si pierdes el acceso a tu billetera sin un respaldo correcto, la pérdida es definitiva.",
        "Muchos usuarios —**incluso avanzados**— caen en la trampa de postergar el respaldo: “luego lo hago”, **“cuando tenga tiempo”**, **“no pasa nada si lo dejo así”**. Hasta que un día el teléfono se rompe, se pierde, se restaura de fábrica o la aplicación falla, y entonces descubren la verdad dura del blockchain: sin seed, no hay dinero.",
        "Este capítulo busca que no formes parte de esas estadísticas. Un buen respaldo puede ser la diferencia entre una experiencia saludable en Web3 y una catástrofe financiera."
      ]
    },
    {
      heading: "¿Qué significa realmente respaldar una billetera?",
      text: [
        "Respaldar tu billetera no es solo anotar 12 palabras en cualquier papel. Respaldar correctamente significa crear, proteger y poder usar una copia fiable de la información que te permite restaurar tu billetera en otro dispositivo.",
        "En la práctica, un respaldo bien hecho incluye:",
        "🔹 Registrar de forma legible y exacta tu frase mnemotécnica (seed phrase).",
        "🔹 Proteger esa frase en uno o varios soportes físicos resistentes (papel, metal, etc.).",
        "🔹 Mantener el respaldo desconectado de Internet (offline) para evitar hackeos.",
        "🔹 Guardar las copias en lugares seguros y separados.",
        "🔹 Probar al menos una vez que puedes restaurar tu billetera usando solo el respaldo.",
        "Un respaldo mal hecho (foto en el celular, archivo en la nube, hoja que cualquiera puede ver) en realidad no es un respaldo: es una invitación abierta al desastre."
      ]
    },
    {
      heading: "El error de confiar en la memoria",
      text: [
        "Un error muy común es creer que puedes memorizar la frase mnemotécnica y prescindir del respaldo físico:",
        "**“Son solo 12 palabras, yo sí me las aprendo”**.",
        "El problema es que la memoria humana no está diseñada para almacenar de forma perfecta una secuencia exacta de palabras durante años, bajo estrés y sin errores. Basta con que olvides una palabra, confundas el orden o cambies una letra para que la frase deje de ser válida.",
        "Además, cuando ocurre un incidente (robo, incendio, bloqueo del dispositivo, enfermedad, mudanza), el estrés psicológico puede borrar o distorsionar recuerdos. En ese momento no quieres depender de tu capacidad de memorización: quieres depender de un respaldo sólido, claro y probado.",
        "Conclusión: memorizar puede ser un complemento, pero nunca un sustituto del respaldo físico."
      ]
    },
    {
      heading: "Métodos recomendados para respaldar tu billetera",
      text: "Existen diversos métodos para respaldar una billetera. Algunos son sencillos y otros más avanzados. Lo importante es entender sus ventajas y limitaciones.",
      subsections: [
        {
          heading: "Respaldo en papel",
          text: [
            "Es el método más básico y ampliamente utilizado. Consiste en escribir a mano tu frase mnemotécnica en una hoja de papel:",
            "🔹 Utiliza bolígrafo de buena calidad, preferentemente tinta indeleble.",
            "🔹 Escribe despacio, revisando ortografía y el orden de las palabras.",
            "🔹 No uses impresoras ni tomes fotos de la hoja; cualquier dispositivo conectado puede ser comprometido.",
            "🔹 Guarda la hoja en un lugar seco, sin exposición directa al sol y lejos de miradas curiosas.",
            "El gran problema del papel es que se puede mojar, quemar, romper o perder. Por eso, suele usarse como primer paso, pero no como única solución a largo plazo."
          ]
        },
        {
          heading: "Placas o tarjetas metálicas",
          text: [
            "Las placas metálicas son un nivel superior de respaldo, diseñadas para resistir fuego, agua, corrosión y golpes. La idea es grabar tu seed en metal:",
            "🔹 Placas de acero o titanio son habituales.",
            "🔹 Puedes grabar, punzonar o usar kits que ya traen letras para formar las palabras.",
            "🔹 Resisten incendios y desastres que destruirían cualquier hoja de papel.",
            "Este tipo de respaldo es ideal para quienes manejarán patrimonio significativo o quieren estar tranquilos durante años. El coste es mayor, pero también lo es la protección."
          ]
        },
        {
          heading: "Redundancia inteligente",
          text: [
            "No basta con un solo respaldo en un único lugar. La regla práctica es:",
            "🔹 Dos copias es prudencia.",
            "🔹 Tres copias es seguridad.",
            "🔹 Más de tres copias empieza a ser un riesgo (hay más puntos de fuga).",
            "La clave es encontrar un equilibrio entre tener copias suficientes para cubrir accidentes (incendio, inundación, robo en una ubicación) y no multiplicar tanto los lugares que aumentes tus posibilidades de filtración."
          ],
          list: [
            "No guardes todas las copias en la misma casa.",
            "No confíes todas las copias a la misma persona.",
            "Evita que cualquier individuo pueda acceder por sí solo a todas las copias sin supervisión."
          ]
        },
        {
          heading: "División de la seed (Shamir Secret Sharing – SSS)",
          text: [
            "Un enfoque más avanzado consiste en dividir tu seed en varias partes, de modo que ninguna parte por sí sola revele la frase completa. Este esquema se conoce como Shamir Secret Sharing.",
            "Ejemplo típico:",
            "🔹 Tienes 3 fragmentos **(A, B y C)**.",
            "🔹 Cualquier combinación de 2 fragmentos permite reconstruir la seed.",
            "🔹 Un solo fragmento no sirve de nada por sí solo.",
            "Ventajas:",
            "🔹 Si alguien encuentra un fragmento, no puede robar tus fondos.",
            "🔹 Puedes distribuir los fragmentos en distintas ubicaciones o personas.",
            "Desventajas:",
            "🔹 Implementarlo mal puede hacer que ni tú mismo puedas reconstruir tu seed.",
            "🔹 Requiere disciplina y buen registro de cómo se dividieron las partes.",
            "Este método es poderoso, pero se recomienda para usuarios que ya tienen cierta experiencia y orden documental."
          ]
        },
        {
          heading: "Caja fuerte y ubicaciones separadas",
          text: [
            "Una estrategia clásica es utilizar cajas fuertes físicas:",
            "🔹 Caja fuerte en tu casa o lugar de trabajo.",
            "🔹 Caja de seguridad en un banco u otra institución.",
            "🔹 Caja fuerte en casa de un familiar o socio de extrema confianza.",
            "La idea es que no dependas de un solo entorno (ni de una sola ciudad). Si una ubicación se vuelve inaccesible por robo, desastre natural o causas legales, todavía tengas otra copia en otro lugar."
          ]
        }
      ]
    },
    {
      heading: "Errores graves en el respaldo (aunque parezcan cómodos)",
      text: "Estos errores son extremadamente frecuentes y han provocado pérdidas millonarias. Léelos con cuidado y verifica si estás cometiendo alguno.",
      list: [
        "Guardar la seed como foto en la galería del celular.",
        "Escribir la frase en un archivo de texto o PDF dentro de la computadora.",
        "Almacenar la seed en servicios en la nube (Google Drive, iCloud, Dropbox, etc.).",
        "Enviarse la seed por correo electrónico o mensajería (WhatsApp, Telegram, etc.).",
        "Guardar la seed en un USB sin cifrado y dejarlo en cualquier cajón.",
        "Mostrar la seed en videollamadas o compartir pantalla sin cuidado.",
        "Pegar la seed en una nota adhesiva cerca del escritorio o en un lugar visible.",
        "Tomar captura de pantalla del proceso de creación de la billetera y confiar en que “nadie verá esa imagen”."
      ]
    },
    {
      heading: "¿Cómo se pierden fondos por respaldos inseguros?",
      text: [
        "En la práctica, una gran parte de los robos en el ecosistema cripto ocurren porque el atacante obtiene la seed del usuario a través de un respaldo inseguro. Algunos escenarios reales:",
        "🔹 Malware que escanea el disco duro buscando palabras clave como “seed”, “mnemonic”, “wallet”, “private key” y extrae archivos de texto o imágenes donde aparece la frase.",
        "🔹 Servicios de nube comprometidos, donde un atacante accede a las fotos o documentos guardados sin cifrado.",
        "🔹 Phishing dirigido, en el que la víctima comparte la seed “para verificar la cuenta” con alguien que finge ser soporte técnico.",
        "🔹 Parejas, amigos o conocidos que ven la seed escrita en una hoja, toman una foto sin avisar, y más tarde vacían la billetera.",
        "🔹 Dispositivos vendidos o regalados sin un borrado seguro, donde aún queda un archivo con la seed o una exportación de la wallet.",
        "Ninguna de estas situaciones requiere vulnerar la criptografía de la blockchain. Basta con vulnerar la seguridad del usuario. Por eso el respaldo debe considerarse parte central de tu modelo de seguridad."
      ]
    },
    {
      heading: "¿Cómo crear un respaldo a prueba de errores (guía paso a paso)?",
      text: [
        "A continuación, un flujo recomendado para crear un respaldo mucho más robusto que el promedio. Puedes adaptarlo a tu realidad, pero intenta respetar la esencia.",
        "**Paso 1** — Crea la billetera en un entorno lo más limpio posible:",
        "🔹 Dispositivo libre de malware, actualizado y preferentemente dedicado a tareas financieras.",
        "🔹 Sin transmisiones de pantalla, sin gente alrededor, sin cámaras apuntando a tu monitor o a tu escritorio.",
        "**Paso 2**— Anota la seed a mano:",
        "🔹Escribe cada palabra con calma, revisando ortografía y orden.",
        "🔹 Repite el proceso en una segunda hoja o soporte, comparando palabra por palabra.",
        "**Paso 3** — Transfiere la seed a un soporte metálico (opcional pero muy recomendado si manejarás grandes montos):",
        "🔹 Usa una placa de acero o titanio y graba o punzona las palabras.",
        "**Paso 4** — Crea redundancia:",
        "🔹 Ten al menos dos copias físicas en ubicaciones distintas.",
        "🔹 Si usas división de secretos (Shamir), documenta claramente cuántas partes existen y cuántas se necesitan para reconstruir.",
        "**Paso 5** — Protege el acceso físico:",
        "🔹 Guarda las copias en cajas fuertes o lugares de seguridad equivalente.",
        "🔹 Evita que una sola persona sin tu consentimiento pueda acceder a todas las copias.",
        "**Paso 6** — Documenta tu esquema de respaldo:",
        "🔹 Anota, en un documento físico separado, cómo están organizados tus respaldos (sin escribir la seed en claro).",
        "🔹 Ejemplo: “Placa A: casa. Placa B: caja bancaria. Fragmento C: familiar X.”",
        "**Paso 7** — Revisa al menos una vez al año:",
        "🔹 Comprueba que los lugares donde guardas tus respaldos siguen existiendo y siendo accesibles.",
        "🔹 Considera actualizaciones si cambias de domicilio, país o situación familiar."
      ]
    },
    {
      heading: "La prueba que casi nadie hace (y deberías hacer)",
      text: [
        "Antes de enviar fondos significativos a una billetera, deberías hacer un ensayo general de recuperación:",
        "🔹 Crea una nueva billetera de prueba.",
        "🔹 Anota su seed siguiendo tus propias reglas de respaldo.",
        "🔹 Elimina la billetera del dispositivo.",
        "🔹 Intenta restaurarla únicamente usando la seed que anotaste.",
        "Si la billetera se restaura correctamente, con las mismas direcciones y acceso a los fondos de prueba, significa que tu método de respaldo funciona. Si tienes problemas durante esta prueba (palabras ilegibles, dudas con el orden, confusión con tu sistema de fragmentación), es mejor descubrirlo ahora que cuando la billetera ya tenga gran valor.",
        "Este pequeño experimento puede revelar fallos ocultos en tu estrategia de respaldo y darte tiempo para corregirlos antes de que haya demasiado en juego."
      ]
    },
    {
      heading: "Por qué este capítulo vale más que cualquier otro",
      text: [
        "Puedes cometer errores al firmar transacciones, caer en un phishing o interactuar con un contrato malicioso. Muchos de esos errores pueden mitigarse si cuentas con monitoreo, límites y buenas prácticas. Pero hay algo que no perdona: perder tu seed o exponerla de forma irreversible.",
        "Un buen respaldo es tu seguro de vida en el mundo cripto. No te protege de todos los problemas, pero sí de los más definitivos: los que te dejarían sin forma de recuperar tus activos.",
        "Si sales de este libro con un solo hábito nuevo, que sea este: cada vez que crees una billetera o manejes activos relevantes, pregúntate: “¿Qué pasaría si hoy pierdo este dispositivo?” Si la respuesta no te gusta, aún no tienes un verdadero respaldo."
      ]
    },
    {
      heading: "Mensaje final: disciplina antes que arrepentimiento",
      text: [
        "Respaldar bien tu billetera no es glamuroso. No presume rendimientos, no genera pantallazos para redes sociales, no es tema de conversación en las fiestas. Pero es lo que separa a los sobrevivientes del ecosistema de las estadísticas de víctimas.",
        "La autoprotección en blockchain no es un truco ni una app mágica: es disciplina, hábitos y procesos. Y todo comienza por tomar en serio el respaldo de tu billetera.",
        "Dedica el tiempo necesario, define tu estrategia y ejecútala con calma. El día que ocurra un imprevisto —porque siempre ocurre alguno— te alegrarás de haber hecho este trabajo hoy, y no de haberlo dejado para “después”."
      ]
    }
  ]
};

export default CAP7;
